'use client';

import { motion, AnimatePresence } from 'motion/react';
import { useEffect, useState, useTransition } from 'react';

import styles from './RotatingTicker.module.scss';

interface RotatingTickerProps {
  items: string[];
  interval?: number;
  style?: React.CSSProperties;
  showControls?: boolean;
}

const RotatingTicker = ({
  items,
  interval = 3000,
  style = {},
  showControls = false
}: RotatingTickerProps) => {
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [isPending, startTransition] = useTransition();
  const [isPaused, setIsPaused] = useState<boolean>(false);

  useEffect(() => {
    if (isPaused) return;

    const tickerInterval = setInterval(() => {
      startTransition(() => {
        setCurrentIndex((prevIndex) => (prevIndex + 1) % items.length);
      });
    }, interval);

    return () => clearInterval(tickerInterval);
  }, [items.length, interval, startTransition, isPaused]);

  const goToPrevious = () => {
    startTransition(() => {
      setCurrentIndex((prevIndex) =>
        prevIndex === 0 ? items.length - 1 : prevIndex - 1
      );
    });
  };

  const goToNext = () => {
    startTransition(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % items.length);
    });
  };

  const variants = {
    enter: {
      y: 20,
      opacity: 0,
    },
    active: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.3 },
    },
    exit: {
      y: -20,
      opacity: 0,
      transition: { duration: 0.3 },
    }
  };

  return (
    <div
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      <AnimatePresence mode="wait">
        <motion.div
          key={currentIndex}
          variants={variants}
          initial="enter"
          animate="active"
          exit="exit"
          style={style}
        >
          {items[currentIndex]}
        </motion.div>
      </AnimatePresence>

      {showControls && (
        <div>
          <div className={styles.navigation}>
            <button
              onClick={goToNext}
              className={styles.btn__prev}
              disabled={isPending}
            >
            </button>
            <button
              onClick={goToPrevious}
              className={styles.btn__next}
              disabled={isPending}
            >
            </button>
          </div>
          <div style={{ display: 'none' }}>
            <span className="text-xs text-gray-500">
              {currentIndex + 1} / {items.length}
            </span>
          </div>
        </div>
      )}
    </div>
  )
};

export default RotatingTicker;